import { readSetting, writeSetting } from '../../infrastructure/storage/localStore';

/**
 * The best runs this DEVICE has ever had (SPEC-007 PR-7.7).
 *
 * A device rather than an account, and this is the one place in the feature
 * where that has to be said out loud rather than assumed: a "leaderboard"
 * normally means other people, and this one cannot. Legends has no backend and
 * is not getting one (ADR-0004), so there is no server to hold a shared board,
 * no identity to attach a name to, and nothing to stop a reader from writing any
 * number they like into their own storage. What this IS, honestly: the five best
 * runs on this phone, in this browser, and clearing site data forgets them. The
 * screen says "on this device" for exactly that reason.
 *
 * Stored as JSON through `localStore`, which is the only module in the app that
 * touches Web Storage and the only one that knows Safari's private mode throws
 * on the accessor rather than returning null. Nothing sensitive: it is a list of
 * scores (CON-003).
 */

const LEADERBOARD = 'duel.leaderboard';

/**
 * Five. Long enough that a good run stays on the board for a while, short enough
 * to draw in the space a screen that does not scroll can spare for it.
 */
export const BOARD_SIZE = 5;

/** A row the screen can draw, or nothing. Anything malformed is nothing. */
function readEntry(entry) {
    const score = Number(entry?.score);
    const streak = Number(entry?.streak);

    if (!Number.isFinite(score) || score <= 0 || !Number.isFinite(streak) || streak < 0) {
        return null;
    }

    return { score: Math.trunc(score), streak: Math.trunc(streak) };
}

/**
 * The board, best first.
 *
 * Everything unreadable comes back as an empty board rather than throwing: this
 * is parsed from a string a previous build wrote, a hand-edited storage entry,
 * or a future build with a different shape, and a board that could take the
 * screen down is a worse outcome than a board that forgot itself.
 */
export function readLeaderboard() {
    try {
        const stored = JSON.parse(readSetting(LEADERBOARD) ?? '[]');

        return (Array.isArray(stored) ? stored : [])
            .map(readEntry)
            .filter(Boolean)
            .sort((one, other) => other.score - one.score)
            .slice(0, BOARD_SIZE);
    } catch (error) {
        return [];
    }
}

/**
 * Put a finished run on the board.
 *
 * Returns the board it produced AND where this run landed on it, because the
 * screen needs both and deriving the placing from the board afterwards would
 * mean matching on a score — which is ambiguous the moment two runs tie, and two
 * runs tying is the normal case at the bottom of a five-row board.
 *
 * A run worth nothing is not recorded. A board whose fifth row is a zero is a
 * board that tells a new reader their first attempt was one of their best five,
 * which is true and useless.
 *
 * @returns {{board: Array, rank: number|null}} `rank` is 1-based, or null for a
 *   run that did not make the board.
 */
export function recordRun({ score, streak }) {
    const entry = readEntry({ score, streak });
    const board = readLeaderboard();

    if (!entry) {
        return { board, rank: null };
    }

    // A run that TIES the score below it goes underneath, not above: the older
    // run got there first, and a board that reshuffled itself on a tie would
    // move rows a reader was looking at for no reason they could see.
    const rank = board.filter((row) => row.score >= entry.score).length + 1;

    if (rank > BOARD_SIZE) {
        return { board, rank: null };
    }

    const updated = [...board.slice(0, rank - 1), entry, ...board.slice(rank - 1)].slice(0, BOARD_SIZE);

    writeSetting(LEADERBOARD, JSON.stringify(updated));

    return { board: updated, rank };
}

/** The longest streak on the board — the record the scoreboard shows. */
export function bestStreak(board = readLeaderboard()) {
    return board.reduce((best, row) => Math.max(best, row.streak), 0);
}

/**
 * The highest score on the board — the record the collection's ladder is quoted
 * against (SPEC-010 D-2).
 *
 * Read off the board rather than kept as a counter of its own, and that is the
 * whole point of putting it here: the board already holds this number and
 * already sorts by it, so a `duel.record` key written beside it would be a
 * second copy of one fact. Two copies is how a reader ends up being told to beat
 * 3,200 while the board above the sentence shows 4,100.
 *
 * `board[0]` would do it, and the reduce is here anyway so that a board somebody
 * hand-edited out of order still answers the largest number in it rather than
 * whichever one happens to be first.
 */
export function bestScore(board = readLeaderboard()) {
    return board.reduce((best, row) => Math.max(best, row.score), 0);
}
